import type { ScreenLocationFieldOption } from "../fields/form-entity-select";
import type { RuleVariable } from "../hooks/useRuleVariables";
import type { ScreenLocationOption } from "../modals/CreateRuleModal";
import type { TableOption } from "../types/configs";
import type { LoopNodeData } from "../types/nodes";

function asRecord(value: unknown): Record<string, unknown> | null {
	if (value != null && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return null;
}

function screenLocationIdFromRecord(record: Record<string, unknown>): string | number | null {
	const id = record.screenLocationId;
	if (id == null || String(id).trim() === "") return null;
	if (typeof id !== "string" && typeof id !== "number") return null;
	return id;
}

/** Resolve display location from a screen table row's `screenLocationId` (tables API). */
export function locationOptionFromScreenTable(
	table: Record<string, unknown>,
	screenLocationOptions: ScreenLocationOption[] = [],
): ScreenLocationFieldOption | null {
	const screenLocationId = screenLocationIdFromRecord(table);
	if (screenLocationId == null) return null;

	const fromOptions = screenLocationOptions.find(
		(loc) => String(loc.id) === String(screenLocationId),
	);
	if (fromOptions) {
		return { id: fromOptions.id, name: fromOptions.name };
	}

	const explicitName = table.screenLocationName;
	if (explicitName != null && String(explicitName).trim() !== "") {
		return { id: screenLocationId, name: String(explicitName).trim() };
	}

	return null;
}

function findScreenTableInGroups(
	tableGroups: unknown[],
	screenTableId: string,
): Record<string, unknown> | null {
	const id = String(screenTableId).trim();
	for (const raw of tableGroups) {
		const group = asRecord(raw);
		if (!group || !Array.isArray(group.screenTables)) continue;
		for (const rawTable of group.screenTables) {
			const table = asRecord(rawTable);
			if (!table || table.id == null) continue;
			if (String(table.id) === id) return table;
		}
	}
	return null;
}

/** Maps screen table id → owning screen location using each table's `screenLocationId`. */
export function buildScreenTableLocationIndex(
	tableGroups: unknown[],
	screenLocationOptions: ScreenLocationOption[] = [],
): Map<string, ScreenLocationFieldOption> {
	const index = new Map<string, ScreenLocationFieldOption>();

	for (const raw of tableGroups) {
		const group = asRecord(raw);
		if (!group || !Array.isArray(group.screenTables)) continue;

		for (const rawTable of group.screenTables) {
			const table = asRecord(rawTable);
			if (!table || table.id == null) continue;
			const location = locationOptionFromScreenTable(table, screenLocationOptions);
			if (!location) continue;
			index.set(String(table.id), location);
		}
	}

	return index;
}

/** Split screen tables into rule-screen vs other-screen groups for LoopForm. */
export function partitionScreenTablesForLoop(
	tableGroups: unknown[],
	ruleScreenLocationId?: string | number,
): { currentScreenTables: TableOption[]; otherScreenTables: TableOption[] } {
	const currentScreenTables: TableOption[] = [];
	const otherScreenTables: TableOption[] = [];
	const hasRuleScreen =
		ruleScreenLocationId != null && String(ruleScreenLocationId).trim() !== "";

	for (const raw of tableGroups) {
		const group = asRecord(raw);
		if (!group || !Array.isArray(group.screenTables)) continue;

		for (const rawTable of group.screenTables) {
			const table = asRecord(rawTable);
			if (!table || table.id == null) continue;

			const option: TableOption = {
				label: String(table.name ?? ""),
				value: String(table.id),
				source: "screen",
			};

			if (!hasRuleScreen) {
				currentScreenTables.push(option);
				continue;
			}

			const tableLocationId = screenLocationIdFromRecord(table);
			if (
				tableLocationId != null &&
				String(tableLocationId) === String(ruleScreenLocationId)
			) {
				currentScreenTables.push(option);
			} else {
				otherScreenTables.push(option);
			}
		}
	}

	return { currentScreenTables, otherScreenTables };
}

/** Resolve screen table id from tableGroups when the loop node only persisted a name. */
export function resolveScreenTableIdFromGroups(
	tableGroups: unknown[],
	screenTableName?: string,
): string | undefined {
	const name = screenTableName?.trim();
	if (!name) return undefined;

	for (const raw of tableGroups) {
		const group = asRecord(raw);
		if (!group || !Array.isArray(group.screenTables)) continue;
		for (const rawTable of group.screenTables) {
			const table = asRecord(rawTable);
			if (!table || table.id == null) continue;
			if (String(table.name ?? "").toLowerCase() === name.toLowerCase()) {
				return String(table.id);
			}
		}
	}
	return undefined;
}

export interface SuggestedScreenLocationOptions {
	screenLocationOptions?: ScreenLocationOption[];
	tableGroups?: unknown[];
}

/** Screen location to suggest in loop decision FormEntitySelect (screen-table loops only). */
export function resolveSuggestedScreenLocationForLoop(
	loopIterateType: "variable" | "table",
	screenTableId: string | undefined,
	index: Map<string, ScreenLocationFieldOption>,
	options: SuggestedScreenLocationOptions = {},
): ScreenLocationFieldOption[] {
	if (loopIterateType !== "table") return [];
	if (!screenTableId?.trim()) return [];

	const fromIndex = index.get(String(screenTableId).trim());
	if (fromIndex) return [fromIndex];

	const { screenLocationOptions = [], tableGroups = [] } = options;
	const table = findScreenTableInGroups(tableGroups, screenTableId);
	if (!table) return [];

	const location = locationOptionFromScreenTable(table, screenLocationOptions);
	return location ? [location] : [];
}

/** Stable id for the synthetic "Current Row - …" single-row database variable in loop context. */
export const LOOP_CURRENT_ROW_SINGLE_ROW_VARIABLE_ID = "__loop-current-row-single__";

/**
 * Resolves the multi-row database table variable a loop iterates (not direct DB/screen table picks).
 */
export function resolveIteratedDatabaseMultiFromLoopNodeData(
	d: LoopNodeData,
	pool: RuleVariable[],
): RuleVariable | null {
	const hasDbTableVariableIterator =
		Boolean(d.databaseTableVariableName?.trim()) ||
		(d.databaseTableVariableId != null && String(d.databaseTableVariableId).trim() !== "");

	if (!hasDbTableVariableIterator) return null;

	const byName = d.databaseTableVariableName?.trim();
	if (byName) {
		const hit = pool.find((v) => v.name === byName);
		if (hit) return hit;
		return {
			id: `databaseVariable-${byName}`,
			name: byName,
			type: "databaseMulti",
		};
	}

	const vid = d.databaseTableVariableId;
	if (vid != null && String(vid).trim() !== "") {
		const idStr = String(vid);
		return pool.find((v) => v.id === idStr || v.id === `dbMulti-${idStr}`) ?? null;
	}

	return null;
}

/** Single-row shape for toolbox + FormEntitySelect (resource / value) in a loop body. */
export function loopCurrentRowAsSingleRowDatabaseVariable(iteratedMulti: RuleVariable): RuleVariable {
	const baseName = iteratedMulti.name?.trim() ?? "";
	return {
		...iteratedMulti,
		id: LOOP_CURRENT_ROW_SINGLE_ROW_VARIABLE_ID,
		name: `Current Row - ${baseName}`,
		type: "databaseSingle",
		recordChoice: "first",
	};
}
